import React from 'react'
import { useNavigate, useParams } from 'react-router'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import { useGetCreditApplicationForReviewSuspenseQuery } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { formatDateForDisplay } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const AuthorizedSignerReviewSection = () => {
  const navigate = useNavigate()
  // this control is just so we can not use legacy form context
  const { control } = useForm()
  const { appId } = useParams()
  const [getClassName] = useNameFormatter('AuthorizedSignerReviewSection')
  const { track } = useWebTracking()

  const { data } = useGetCreditApplicationForReviewSuspenseQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const {
    firstName,
    lastName,
    title,
    dob,
    email,
    passportCountry,
    passportNumberLength,
    passportNumberLastFour,
    passportExpiration,
    phoneNumber,
    ssnLastFour,
    address,
    id: authSignerId,
  } = data?.creditApplication?.authorizedSigner || {}

  const showPassportSection = (passportCountry || passportNumberLastFour || passportExpiration) && !ssnLastFour

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField([
      firstName,
      lastName,
      title,
      dob,
      email,
      phoneNumber,
      address?.street,
      address?.city,
      address?.state,
      address?.postalCode,
      ...(showPassportSection ? [passportCountry, passportNumberLastFour, passportExpiration] : [ssnLastFour]),
    ])

  const handleOnEditClick = () => {
    track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Authorized Signer',
      navigated_to_page: `${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`,
    })

    navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}${authSignerId ? `?personId=${authSignerId}` : ''}`)
  }

  return (
    <div className={`${styles[getClassName('')]} dd-privacy-allow`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Authorized signer'
            id='sputnik.AuthorizedSignerReviewSection__YVlBeJ'
          />
        }
        onEditClick={handleOnEditClick}
      >
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(firstName)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='First name'
                id='sputnik.AuthorizedSignerReviewSection__16D5H3'
              />
            </span>
          }
          name='authSignerFirstName'
          value={firstName ? toSentenceCase(firstName) : '—'}
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(lastName)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='Last name'
                id='sputnik.AuthorizedSignerReviewSection__dsX8S3'
              />
            </span>
          }
          name='authSignerLastName'
          value={lastName ? toSentenceCase(lastName) : '—'}
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(title)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='Title'
                id='sputnik.AuthorizedSignerReviewSection__bUcdiS'
              />
            </span>
          }
          name='authSignerTitle'
          value={title ?? '—'}
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(dob)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='Date of birth'
                id='sputnik.AuthorizedSignerReviewSection__GFTdXw'
              />
            </span>
          }
          name='authSignerDOB'
          value={dob ? formatDateForDisplay(dob) : '—'}
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(email)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='Business email'
                id='sputnik.AuthorizedSignerReviewSection__Z7xDUp'
              />
            </span>
          }
          name='authSignerEmail'
          value={email ?? '—'}
        />
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(phoneNumber)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='Mobile phone number'
                id='sputnik.AuthorizedSignerReviewSection__h8O5Rm'
              />
            </span>
          }
          name='authSignerPhoneNumber'
          value={
            phoneNumber
              ? isValidPhoneNumber(phoneNumber)
                ? parsePhoneNumber(phoneNumber).formatNational()
                : phoneNumber
              : '—'
          }
        />
        <span className={styles[getClassName('street')]}>
          <FormTextInput
            readOnly
            className='dd-privacy-mask'
            control={control}
            isRequired={hasEmptyValue(address?.street)}
            label={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='Residential address'
                  id='sputnik.AuthorizedSignerReviewSection__4iXhxD'
                />
              </span>
            }
            name='authSignerResidentialStreet'
            value={address?.street ? `${address?.street || ''} ${address?.streetAdditional || ''}` : '—'}
          />
        </span>
        <div className={styles[getClassName('city-state')]}>
          <FormTextInput
            readOnly
            className='dd-privacy-mask'
            control={control}
            isRequired={hasEmptyValue(address?.city)}
            label={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='City'
                  id='sputnik.AuthorizedSignerReviewSection__TE4fIS'
                />
              </span>
            }
            name='authSignerResidentialCity'
            value={address?.city ? toSentenceCase(address?.city) : '—'}
          />
          <FormTextInput
            readOnly
            className='dd-privacy-mask'
            control={control}
            isRequired={hasEmptyValue(address?.state)}
            label={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='State'
                  id='sputnik.AuthorizedSignerReviewSection__ku+mDU'
                />
              </span>
            }
            name='authSignerResidentialState'
            value={address?.state ?? '—'}
          />
        </div>
        <FormTextInput
          readOnly
          className='dd-privacy-mask'
          control={control}
          isRequired={hasEmptyValue(address?.postalCode)}
          label={
            <span className='dd-privacy-allow'>
              <FormattedMessage
                defaultMessage='ZIP code'
                id='sputnik.AuthorizedSignerReviewSection__iZL8VQ'
              />
            </span>
          }
          name='authSignerResidentialPostalCode'
          value={address?.postalCode ?? '—'}
        />
        {showPassportSection && (
          <>
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(passportNumberLastFour)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Passport number'
                    id='sputnik.AuthorizedSignerReviewSection__7DeEPg'
                  />
                </span>
              }
              name='authSignerPassportNumberLastFour'
              value={
                passportNumberLastFour
                  ? `${new Array(passportNumberLength ?? 5).fill('*').join('')}${passportNumberLastFour}`
                  : '—'
              }
            />
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(passportCountry)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Issuing country'
                    id='sputnik.AuthorizedSignerReviewSection__GdcAVX'
                  />
                </span>
              }
              name='authSignerPassportCountry'
              value={passportCountry ?? '—'}
            />
            <FormTextInput
              readOnly
              className='dd-privacy-mask'
              control={control}
              isRequired={hasEmptyValue(passportExpiration)}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Expiration date'
                    id='sputnik.AuthorizedSignerReviewSection__CICBj0'
                  />
                </span>
              }
              name='authSignerPassportExpiration'
              value={formatDateForDisplay(passportExpiration) || '—'}
            />
          </>
        )}
        {!showPassportSection && (
          <FormTextInput
            readOnly
            className='dd-privacy-mask'
            control={control}
            isRequired={hasEmptyValue(ssnLastFour)}
            label={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='Social Security number'
                  id='sputnik.AuthorizedSignerReviewSection__Pk5sml'
                />
              </span>
            }
            name='authSignerSsnLastFour'
            value={ssnLastFour ? `***-**-${ssnLastFour}` : '—'}
          />
        )}
      </SectionCollapsibleList>
    </div>
  )
}

export default AuthorizedSignerReviewSection
